import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, getDocs, doc, getDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import DateRangeSelector from '../components/common/DateRangeSelector';
import { getDateRange, formatDate, validateDateRange } from '../utils/dateHelpers';
import { useAuth } from '../components/auth/AuthContext';
import { monitorQuery, monitorParallelQueries } from '../utils/queryMonitoring';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';
import CompactChart from '../components/common/CompactChart';
import { useUserRole } from '../hooks/useUserRole';
import { usePermissions } from '../hooks/usePermissions';
import { canViewAllData, hasPermission, FEATURE_PERMISSIONS, getRbacMatchNames } from '../utils/permissions';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import ExportButton from '../components/common/ExportButton';
import { useUrlState } from '../hooks/useUrlState';
import { exportData, exportMultiSectionData } from '../utils/exportUtils';
import { createRepResolver, createCustomerLookup, visibleCustomerOrders } from '../utils/reportOwnership';

const CustomerReportsPage = ({ user }) => {
    const { currentUser } = useAuth();
    const userRole = useUserRole(user);
    const { can } = usePermissions(user);
    const viewAllReports = canViewAllData(user) || can(FEATURE_PERMISSIONS.REPORTS_VIEW_ALL_REPS);
    const [customers, setCustomers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [range, setRange] = useUrlState('range', "30");
    const [customStart, setCustomStart] = useUrlState('customStart', "");
    const [customEnd, setCustomEnd] = useUrlState('customEnd', "");
    const [selectedReps, setSelectedReps] = useUrlState('selectedReps', []);
    // Pending filter state — all filters wait for Apply button
    const [pendingRange, setPendingRange] = useState(range || '30');
    const [pendingCustomStart, setPendingCustomStart] = useState(customStart || '');
    const [pendingCustomEnd, setPendingCustomEnd] = useState(customEnd || '');
    const [pendingReps, setPendingReps] = useState(selectedReps);
    const [showRepDropdown, setShowRepDropdown] = useState(false);
    const [usingCache, setUsingCache] = useState(false);
    const [cacheAge, setCacheAge] = useState(null);
    const [cachedData, setCachedData] = useState(null);
    const repDropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (repDropdownRef.current && !repDropdownRef.current.contains(event.target)) {
                setShowRepDropdown(false);
            }
        };

        if (showRepDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showRepDropdown]);

    // Initialize pendingReps when dropdown opens
    useEffect(() => {
        setPendingReps(selectedReps);
    }, [selectedReps]);

    // Apply rep filter changes (from dropdown)
    const handleApplyRepFilter = () => {
        setShowRepDropdown(false);
    };

    // Cancel rep filter changes
    const handleCancelRepFilter = () => {
        setPendingReps(selectedReps);
        setShowRepDropdown(false);
    };

    // Detect when any pending filter differs from applied
    const filtersChanged = useMemo(() => {
        const repChanged = JSON.stringify(pendingReps) !== JSON.stringify(selectedReps);
        const dateChanged = pendingRange !== range || pendingCustomStart !== customStart || pendingCustomEnd !== customEnd;
        return repChanged || dateChanged;
    }, [pendingReps, selectedReps, pendingRange, range, pendingCustomStart, customStart, pendingCustomEnd, customEnd]);

    const hasActiveFilters = useMemo(() => {
        return pendingRange !== '30' || pendingCustomStart || pendingCustomEnd || pendingReps.length > 0;
    }, [pendingRange, pendingCustomStart, pendingCustomEnd, pendingReps]);

    // Apply all filters — copies pending to URL state (batched via microtask)
    const handleApplyFilters = () => {
        setRange(pendingRange);
        setCustomStart(pendingCustomStart);
        setCustomEnd(pendingCustomEnd);
        setSelectedReps(pendingReps);
    };

    const handleClearFilters = () => {
        setPendingRange('30');
        setPendingCustomStart('');
        setPendingCustomEnd('');
        setPendingReps([]);
        setRange('30');
        setCustomStart('');
        setCustomEnd('');
        setSelectedReps([]);
    };

    // Sync pending state from URL on external changes (browser back/forward)
    useEffect(() => { setPendingRange(range || '30'); }, [range]);
    useEffect(() => { setPendingCustomStart(customStart || ''); }, [customStart]);
    useEffect(() => { setPendingCustomEnd(customEnd || ''); }, [customEnd]);

    useEffect(() => {
        if (!user || !user.uid) return;
        if (userRole === undefined || userRole === null) return; // Wait for role to load

        const fetchData = async () => {
            // Skip fetching if custom range is selected but dates aren't set yet
            if (range === 'custom' && (!customStart || !customEnd)) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                console.log('[CustomerReports] Fetching with userRole:', userRole);
                // Validate date range before fetching
                const validation = validateDateRange(range, customStart, customEnd);
                if (!validation.isValid) {
                    setToast({ message: validation.error, type: 'error' });
                    setLoading(false);
                    return;
                }

                // Get date range
                const { start, end } = getDateRange(range, customStart, customEnd);
                const isStandardRange = ['7','14', '30', '90', '180', '365', 'ytd'].includes(range);

                // Cache stores only the top 100 orders by $ amount (Firestore 1MB doc
                // limit). Non-admin users MUST bypass the cache \u2014 filtering the top-100
                // list to their assigned orders drops most of their real orders and
                // produces totals that don't match Order Reports / Overview (which pull
                // full date_internal-filtered slices from Firestore).
                const canUseCache = viewAllReports || can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL);

                // Filtered reports require complete orders, not the cache's top-100 sample.
                if (isStandardRange && canUseCache && selectedReps.length === 0) {
                    const cacheId = range === 'ytd' 
                        ? 'summary_ytd' 
                        : `summary_${range.padStart(2, '0')}d`; // Pad with zero (e.g., '7' -> '07d')

                    try {
                        const cacheDoc = await getDoc(doc(db, collectionPath('reportsCache'), cacheId));
                        
                        if (cacheDoc.exists()) {
                            const data = cacheDoc.data();
                            const now = new Date();
                            const lastUpdated = data.lastUpdated?.toDate?.() || new Date(0);
                            const ageMinutes = Math.floor((now - lastUpdated) / (1000 * 60));
                            
                            // Use cache if less than 12 hours old (generation runs every 8 hours)
                            if (ageMinutes < 720) {
                                console.log(`[CustomerReports] ✅ Using cached data (${ageMinutes} min old, cache ID: ${cacheId})`);
                                setCachedData(data);
                                setUsingCache(true);
                                setCacheAge(ageMinutes);
                                
                                // Still need to fetch customers for lookups
                                const customersSnap = await getDocs(collection(db, collectionPath('customers')));
                                const customersData = customersSnap.docs.map(doc => ({
                                    id: doc.id,
                                    ...doc.data()
                                }));
                                setCustomers(customersData);
                                
                                // Use cached order data instead of fetching all orders
                                // RBAC: Filter cached orders for non-admin users
                                if (data.ordersForRepFilter) {
                                    let cachedOrders = data.ordersForRepFilter;
                                    
                                    console.log('[CustomerReports] BEFORE filtering:', {
                                        userRole,
                                        userDisplayName: user?.displayName,
                                        totalOrders: cachedOrders.length,
                                        willFilter: !viewAllReports && !can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL)
                                    });
                                    
                                    // Wait for userRole to load before filtering
                                    if (!viewAllReports && !can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL)) {
                                        const matchNames = getRbacMatchNames(user);
                                        const matchSet = new Set(matchNames);
                                        const beforeFilter = cachedOrders.length;
                                        cachedOrders = cachedOrders.filter(order =>
                                            order.commissions_displayNames?.some(name => matchSet.has(name.toLowerCase().trim()))
                                        );
                                        console.log('[CustomerReports] Cache RBAC filtered:', {
                                            before: beforeFilter,
                                            after: cachedOrders.length,
                                            matchNames,
                                            userRole
                                        });
                                    } else {
                                        console.log('[CustomerReports] NOT filtering (admin or missing data):', {
                                            userRole,
                                            isAdmin: canViewAllData(user),
                                            hasDisplayName: !!user?.displayName,
                                            totalOrders: cachedOrders.length
                                        });
                                    }
                                    setOrders(cachedOrders);
                                }
                                
                                // Fetch users for rep filter dropdown
                                const usersSnap = await getDocs(collection(db, collectionPath('users')));
                                const usersData = usersSnap.docs.map(doc => ({
                                    uid: doc.id,
                                    ...doc.data()
                                }));
                                setUsers(usersData);
                                
                                setLoading(false);
                                return;
                            } else {
                                // Cache too old, fetch live data
                            }
                        }
                    } catch (cacheErr) {
                        // Cache fetch failed, use live data
                    }
                }

                // Fetch live data
                setUsingCache(false);
                setCacheAge(null);
                setCachedData(null);

                // Build orders query - always use date_internal indexed range filter.
                // date_internal is YYYY-MM-DD and has a Firestore composite index.
                // Previously, standard ranges fell back to limit(5000) without a date
                // filter, which returned an unordered subset and missed recent orders.
                const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                const startDateStr = range === 'custom' ? customStart : toDateStr(start);
                const endDateStr   = range === 'custom' ? customEnd   : toDateStr(end);
                const ordersQuery = query(
                    collection(db, collectionPath('orders')),
                    where('date_internal', '>=', startDateStr),
                    where('date_internal', '<=', endDateStr),
                    orderBy('date_internal', 'desc')
                );

                // Fetch customers, orders, and users in parallel
                const [customersSnap, ordersSnap, usersSnap] = await monitorParallelQueries([
                    { name: 'fetch-customers-customer-reports', fn: () => getDocs(collection(db, collectionPath('customers'))) },
                    { name: 'fetch-orders-customer-reports', fn: () => getDocs(ordersQuery) },
                    { name: 'fetch-users-customer-reports', fn: () => getDocs(collection(db, collectionPath('users'))) }
                ]);

                const customersData = customersSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setCustomers(customersData);

                let ordersData = ordersSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                
                console.log('[CustomerReports] BEFORE live filtering:', {
                    userRole,
                    userDisplayName: user?.displayName,
                    totalOrders: ordersData.length,
                    willFilter: !viewAllReports && !can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL)
                });
                
                // RBAC: Non-admin users only see orders they're assigned to
                // Case-insensitive multi-name matching to align with cache-path filter above.
                if (!viewAllReports && !can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL)) {
                    const matchSet = new Set(getRbacMatchNames(user));
                    const beforeFilter = ordersData.length;
                    ordersData = visibleCustomerOrders(ordersData, customersData, user, false);
                    console.log('[CustomerReports] Live RBAC filtered:', {
                        before: beforeFilter,
                        after: ordersData.length,
                        matchNames: [...matchSet],
                        userRole
                    });
                } else {
                    console.log('[CustomerReports] NOT filtering live data (admin or missing data):', {
                        userRole,
                        isAdmin: canViewAllData(user),
                        hasDisplayName: !!user?.displayName,
                        totalOrders: ordersData.length
                    });
                }
                
                setOrders(ordersData);

                const usersData = usersSnap.docs.map(doc => ({
                    uid: doc.id,
                    ...doc.data()
                }));
                setUsers(usersData);

                setLoading(false);
            } catch (error) {
                console.error('[CustomerReports] Error fetching data:', error);
                setToast({ message: `Error loading data: ${error.message}`, type: 'error' });
                setLoading(false);
            }
        };

        fetchData();
    }, [user, range, customStart, customEnd, selectedReps, userRole, can, viewAllReports]);

    // Manual cache refresh
    const handleRefreshCache = async () => {
        // Check if user can modify data (admin only, not admin view-only)
        if (!hasPermission(user, FEATURE_PERMISSIONS.REPORTS_CACHE_REFRESH)) {
            setToast({ message: 'Only admins can refresh the cache', type: 'error' });
            return;
        }

        // Confirm action
        const confirmed = window.confirm(
            'This will refresh the reports cache for all date ranges. ' +
            'It may take 30-60 seconds. Continue?'
        );
        
        if (!confirmed) return;

        try {
            setLoading(true);
            setToast({ message: 'Refreshing cache... This may take up to 60 seconds.', type: 'success' });
            
            const refreshCacheFunc = httpsCallable(functions, 'refreshReportsCache');
            const result = await refreshCacheFunc();
            
            setToast({ message: 'Cache refreshed successfully! Reloading data...', type: 'success' });
            
            // Wait a moment for toast to show, then reload
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } catch (error) {
            console.error('[CustomerReports] Error refreshing cache:', error);
            setToast({ message: `Error refreshing cache: ${error.message}`, type: 'error' });
            setLoading(false);
        }
    };

    // Date range filtering
    const { start: filterStart, end: filterEnd } = useMemo(() => 
        getDateRange(range, customStart, customEnd), 
        [range, customStart, customEnd]
    );

    // Filter orders by date range
    const filteredOrders = useMemo(() => {
        // If using cache for standard ranges, orders are already filtered by date range
        if (usingCache && range !== 'custom') {
            return orders;
        }
        
        // For all live-data fetches (both custom and standard ranges), the Firestore query
        // already filtered by date_internal server-side — no further client-side filtering needed.
        if (!usingCache) {
            return orders;
        }
        
        // Fallback: filter by date client-side (should not normally reach here)
        return orders.filter(order => {
            // Prefer date_internal (YYYY-MM-DD), then creation_time, then createdAt
            const orderDate = order.date_internal
                ? new Date(order.date_internal)
                : order.creation_time 
                    ? new Date(order.creation_time.replace(' ', 'T'))
                    : order.createdAt?.toDate?.()
                        ? order.createdAt.toDate()
                        : null;
            
            if (!orderDate) return false;
            return orderDate >= filterStart && orderDate <= filterEnd;
        });
    }, [orders, filterStart, filterEnd, usingCache, range]);

    const repResolver = useMemo(() => createRepResolver(users), [users]);
    const customerLookup = useMemo(() => createCustomerLookup(customers), [customers]);

    // Explicit rep filters preserve the order's historical commission attribution.
    const filteredOrdersByRep = useMemo(() => {
        if (selectedReps.length === 0) return filteredOrders;

        return filteredOrders.filter(order => {
            if (!Array.isArray(order.commissions_displayNames)) return false;
            return repResolver.matches(order.commissions_displayNames, selectedReps);
        });
    }, [filteredOrders, selectedReps, repResolver]);

    // Get list of all reps for dropdown
    const allReps = useMemo(() => {
        const names = orders.flatMap(order => order.commissions_displayNames || []);
        if (usingCache) names.push(...(cachedData?.repPerformance || []).map(rep => rep.displayName || rep.name));
        return repResolver.options(names);
    }, [orders, usingCache, cachedData, repResolver]);

    // top customers by focus area (Golf, Tennis, Pickleball, Other)
    const topCustomersByFocus = useMemo(() => {
        // RBAC: Non-admin users must recalculate from filtered orders (don't use cached aggregates)
        // If using cached data, no rep filter, AND admin, return cached top customers
        if (usingCache && cachedData && cachedData.topCustomersByFocus && selectedReps.length === 0) {
            return cachedData.topCustomersByFocus;
        }
        
        // Calculate from live data or recalculate when rep filter is applied
        const focusCategories = ['Golf', 'Tennis', 'Pickleball', 'Other'];
        const result = {};
        
        focusCategories.forEach(focus => {
            // Find customers matching this focus. For the primary categories the customer
            // must have a matching businessFocus value. "Other" catches everything that
            // does not match Golf/Tennis/Pickleball — including customers whose docs are
            // missing businessFocus entirely, so they still appear on Customer Reports.
            const isPrimaryCategory = focus !== 'Other';
            const relevantCustomers = customers.filter(customer => {
                const focusValue = customer.businessFocus;
                const hasFocus = Array.isArray(focusValue) ? focusValue.length > 0 : Boolean(focusValue);
                if (isPrimaryCategory) {
                    if (!hasFocus) return false;
                    if (Array.isArray(focusValue)) return focusValue.includes(focus);
                    return focusValue === focus;
                }
                // 'Other': no focus OR focus not one of the primary categories
                if (!hasFocus) return true;
                if (Array.isArray(focusValue)) {
                    return !focusValue.some(f => ['Golf', 'Tennis', 'Pickleball'].includes(f));
                }
                return !['Golf', 'Tennis', 'Pickleball'].includes(focusValue);
            });
            
            // Calculate revenue for each customer from filtered orders
            const customerRevenue = new Map();
            
            relevantCustomers.forEach(customer => {
                const account = customer.customerAccountNumber || customer.id;
                if (account) {
                    customerRevenue.set(String(account), {
                        id: customer.id,
                        apparelMagicId: customer.apparelMagicId,
                        customerAccountNumber: customer.customerAccountNumber || null,
                        name: customer.customerName || 'Unknown',
                        revenue: 0,
                        orderCount: 0
                    });
                }
            });
            
            // Sum up revenue from orders (use rep-filtered if applicable)
            const ordersToUse = selectedReps.length > 0 ? filteredOrdersByRep : filteredOrders;
            ordersToUse.forEach(order => {
                const customer = customerLookup.forOrder(order);
                const customerId = customer?.customerAccountNumber || customer?.id || order.customerAccountNumber;
                if (!customerId) return;
                
                const customerData = customerRevenue.get(String(customerId));
                if (customerData) {
                    customerData.revenue += Number(order.amount) || 0;
                    customerData.orderCount += 1;
                }
            });
            
            // Sort by revenue and take top 10
            const topCustomers = Array.from(customerRevenue.values())
                .filter(c => c.revenue > 0)
                .sort((a, b) => b.revenue - a.revenue)
                .slice(0, 10);
            
            result[focus] = topCustomers;
        });
        
        return result;
    }, [customers, customerLookup, filteredOrders, filteredOrdersByRep, selectedReps, usingCache, cachedData, userRole]);

    // Top 10 customers by revenue (overall or per-rep)
    const topCustomersByRevenue = useMemo(() => {
        // RBAC: Non-admin users must recalculate from filtered orders (don't use cached aggregates)
        // If using cached data, no rep filter, AND admin, return cached overall top 10
        if (usingCache && cachedData && cachedData.topCustomersByRevenue && selectedReps.length === 0) {
            return cachedData.topCustomersByRevenue.overall || [];
        }
        
        // Calculate from live data
        const customerRevenue = new Map();
        
        // Prefer the canonical account number; use AM internal IDs only for legacy orders.
        customers.forEach(customer => {
            const account = customer.customerAccountNumber || customer.id;
            if (account) {
                customerRevenue.set(String(account), {
                    id: customer.id,
                    apparelMagicId: customer.apparelMagicId,
                    customerAccountNumber: customer.customerAccountNumber || null,
                    name: customer.customerName || 'Unknown',
                    revenue: 0,
                    orderCount: 0
                });
            }
        });
        
        // Sum up revenue from orders (use rep-filtered if applicable)
        const ordersToUse = selectedReps.length > 0 ? filteredOrdersByRep : filteredOrders;
        
        // Fallback: if a matching customer doc isn't loaded, aggregate directly from the
        // order using its denormalised customer_name / customer_id. This ensures every
        // customer that appears on Order Reports also appears on Customer Reports.
        ordersToUse.forEach(order => {
            const customer = customerLookup.forOrder(order);
            const customerId = customer?.customerAccountNumber || customer?.id || order.customerAccountNumber || order.customer_id;
            if (!customerId) return;
            const key = String(customerId);
            let customerData = customerRevenue.get(key);
            if (!customerData) {
                customerData = {
                    id: null,
                    apparelMagicId: order.customer_id || null,
                    customerAccountNumber: order.customerAccountNumber || null,
                    name: order.customer_name || 'Unknown',
                    revenue: 0,
                    orderCount: 0
                };
                customerRevenue.set(key, customerData);
            }
            customerData.revenue += Number(order.amount) || 0;
            customerData.orderCount += 1;
        });
        
        // Sort by revenue descending. Return the top 10 for the highlighted list plus a
        // summary of everything else so the on-screen numbers reconcile against the
        // Overview page (which sums every filtered order without a rank cutoff).
        const sorted = Array.from(customerRevenue.values())
            .filter(c => c.revenue > 0)
            .sort((a, b) => b.revenue - a.revenue);
        const top = sorted.slice(0, 10);
        const rest = sorted.slice(10);
        const otherSummary = rest.length > 0
            ? {
                customerCount: rest.length,
                revenue: rest.reduce((sum, c) => sum + (c.revenue || 0), 0),
                orderCount: rest.reduce((sum, c) => sum + (c.orderCount || 0), 0)
            }
            : null;
        // Attach the aggregate to the returned array so downstream consumers can access
        // it without threading another piece of state through the component.
        top.otherSummary = otherSummary;
        return top;
    }, [customers, customerLookup, filteredOrders, filteredOrdersByRep, selectedReps, usingCache, cachedData, userRole]);

    const exportSections = [
        { key: 'topRevenue', label: 'Top Customers by Revenue' },
        { key: 'focusArea', label: 'Customers by Focus Area' }
    ];

    const handleExport = (format, sectionKey) => {
        const dateLabel = range === 'ytd' ? 'YTD' : `${range}d`;
        const revenueHeaders = ['Rank', 'Customer Name', 'Account Number', 'Order Count', 'Revenue'];
        const revenueRowMapper = (customer, index) => [
            index + 1,
            customer.name || '',
            customer.customerAccountNumber || '',
            customer.orderCount || 0,
            customer.revenue ? `$${customer.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'
        ];
        const focusHeaders = ['Focus Area', 'Rank', 'Customer Name', 'Order Count', 'Revenue'];

        // Build focus area data (all 4 categories in one flat array)
        const focusData = [];
        ['Golf', 'Tennis', 'Pickleball', 'Other'].forEach(focus => {
            const customers = topCustomersByFocus[focus] || [];
            customers.slice(0, 5).forEach((c, i) => {
                focusData.push({ focus, rank: i + 1, name: c.name, orderCount: c.orderCount, revenue: c.revenue });
            });
        });
        const focusRowMapper = (row) => [
            row.focus,
            row.rank,
            row.name || '',
            row.orderCount || 0,
            row.revenue ? `$${row.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'
        ];

        if (sectionKey === 'topRevenue') {
            exportData({
                filename: `customer_reports_top_revenue_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format, headers: revenueHeaders, data: topCustomersByRevenue, rowMapper: revenueRowMapper,
                options: format.toLowerCase() === 'pdf'
                    ? { title: `Top Customers by Revenue (${dateLabel})`, fontSize: 9, orientation: 'landscape', pageSize: 'a4' }
                    : { sheetName: 'Top By Revenue', columnWidths: [8, 30, 15, 12, 15] }
            });
        } else if (sectionKey === 'focusArea') {
            exportData({
                filename: `customer_reports_focus_area_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format, headers: focusHeaders, data: focusData, rowMapper: focusRowMapper,
                options: format.toLowerCase() === 'pdf'
                    ? { title: `Customers by Focus Area (${dateLabel})`, fontSize: 9, orientation: 'landscape', pageSize: 'a4' }
                    : { sheetName: 'By Focus Area', columnWidths: [15, 8, 30, 12, 15] }
            });
        } else {
            // "all" — multi-section export
            exportMultiSectionData({
                filename: `customer_reports_all_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format,
                sections: [
                    { name: 'Top Customers by Revenue', headers: revenueHeaders, data: topCustomersByRevenue, rowMapper: revenueRowMapper, columnWidths: [8, 30, 15, 12, 15] },
                    { name: 'Customers by Focus Area', headers: focusHeaders, data: focusData, rowMapper: focusRowMapper, columnWidths: [15, 8, 30, 12, 15] }
                ],
                options: { title: `Customer Reports (${dateLabel})`, fontSize: 9, orientation: 'landscape', pageSize: 'a4' }
            });
        }
    };

    if (loading) {
        return (
            <main className="flex-grow p-6 overflow-y-auto">
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading customer reports...</div>
                </div>
            </main>
        );
    }

    return (
        <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            
            <div className="mb-6 flex items-start justify-between">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Customer Reports</h2>
                    <p className="text-gray-600">Analyze customer performance by revenue and focus area</p>
                </div>
                {can(FEATURE_PERMISSIONS.REPORTS_EXPORT) && (
                    <ExportButton
                        onExport={handleExport}
                        formats={['csv', 'xlsx', 'pdf']}
                        disabled={loading || topCustomersByRevenue.length === 0}
                        tooltipText="Export customer report data"
                        label="Export"
                        className="bg-indigo-600 hover:bg-indigo-700"
                        sections={exportSections}
                    />
                )}
            </div>

            {/* Filters */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
                <div className="flex flex-col md:flex-row gap-4 items-end">
                    {/* Sales Rep Filter */}
                    {allReps.length > 0 && (
                        <div className="w-full md:w-64">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Filter by Sales Rep
                            </label>
                            <div className="relative" ref={repDropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => setShowRepDropdown(!showRepDropdown)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-left flex items-center justify-between"
                                >
                                    <span className="block truncate">
                                        {pendingReps.length === 0 
                                            ? 'All Sales Reps' 
                                            : pendingReps.length === allReps.length
                                            ? 'All Sales Reps'
                                            : `${pendingReps.length} selected`}
                                    </span>
                                    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>

                    {showRepDropdown && (
                        <div className="absolute z-50 mt-2 w-80 bg-white border border-gray-300 rounded-lg shadow-xl max-h-96 overflow-y-auto">
                            <div className="p-3 border-b border-gray-200 bg-gray-50">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-semibold text-gray-700">Select Sales Reps</span>
                                    <button
                                        onClick={() => {
                                            if (pendingReps.length === allReps.length) {
                                                setPendingReps([]);
                                            } else {
                                                setPendingReps(allReps.map(r => r.name));
                                            }
                                        }}
                                        className="text-xs text-indigo-600 hover:text-indigo-800"
                                    >
                                        {pendingReps.length === allReps.length ? 'Clear All' : 'Select All'}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500">
                                    {pendingReps.length} of {allReps.length} selected
                                </p>
                            </div>
                            
                            <div className="p-2">
                                {allReps.map(rep => (
                                    <label
                                        key={rep.name}
                                        className="flex items-center px-2 py-2 hover:bg-gray-50 rounded cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={repResolver.matches([rep.name], pendingReps)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setPendingReps([...pendingReps, rep.name]);
                                                } else {
                                                    setPendingReps(pendingReps.filter(r => !repResolver.matches([rep.name], [r])));
                                                }
                                            }}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">{rep.name}</span>
                                    </label>
                                ))}
                            </div>

                            <div className="p-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
                                <button
                                    onClick={handleCancelRepFilter}
                                    className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleApplyRepFilter}
                                    className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded hover:bg-indigo-700"
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    )}
                            </div>
                        </div>
                    )}

                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Filter by Date
                        </label>
                        <DateRangeSelector
                            range={pendingRange}
                            setRange={setPendingRange}
                            customStart={pendingCustomStart}
                            setCustomStart={setPendingCustomStart}
                            customEnd={pendingCustomEnd}
                            setCustomEnd={setPendingCustomEnd}
                            hideClear
                            defaultRange="14"
                        />
                    </div>

                    {/* Apply and Clear Buttons */}
                    <div className="flex items-end gap-2">
                        <button
                            onClick={handleApplyFilters}
                            disabled={!filtersChanged}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                filtersChanged
                                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            {filtersChanged ? '✓ Apply Filters' : 'Applied'}
                        </button>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors"
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Cache indicator */}
            {usingCache && cachedData && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-green-800">
                            Using cached data (last updated {cacheAge}m ago)
                        </span>
                    </div>
                    {hasPermission(user, FEATURE_PERMISSIONS.REPORTS_CACHE_REFRESH) && (
                        <button
                            onClick={handleRefreshCache}
                            className="text-sm text-green-700 hover:text-green-900 font-medium"
                        >
                            Refresh Now
                        </button>
                    )}
                </div>
            )}

            {/* top customers by Revenue */}
            <div className="bg-white p-6 rounded-lg shadow mb-6">
                <Tooltip text="Top 10 customers ranked by total revenue in the selected period. Click a customer to view details.">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Top 10 Customers by Revenue</h3>
                </Tooltip>
                
                {topCustomersByRevenue.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No customer data available for this period</p>
                ) : (
                    <div className="space-y-3">
                        {topCustomersByRevenue.map((customer, index) => (
                            <div 
                                key={customer.id}
                                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`
                                        flex items-center justify-center w-10 h-10 rounded-full font-bold text-white
                                        ${index === 0 ? 'bg-yellow-500' : ''}
                                        ${index === 1 ? 'bg-gray-400' : ''}
                                        ${index === 2 ? 'bg-orange-600' : ''}
                                        ${index > 2 ? 'bg-emerald-500' : ''}
                                    `}>
                                        {index + 1}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">{customer.name}</div>
                                        <div className="text-sm text-gray-500">{customer.orderCount} orders</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xl font-bold text-emerald-600">
                                        ${(customer.revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {topCustomersByRevenue.otherSummary && (
                            <div
                                className="flex items-center justify-between p-4 border border-dashed border-gray-300 rounded-lg bg-gray-50"
                                title="Aggregated revenue and order count for customers ranked outside the top 10. Included so the totals on this page reconcile with the Overview / Order Reports pages."
                            >
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full font-bold text-gray-600 bg-gray-200">
                                        +
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-700">
                                            Other ({topCustomersByRevenue.otherSummary.customerCount} customer{topCustomersByRevenue.otherSummary.customerCount === 1 ? '' : 's'})
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {topCustomersByRevenue.otherSummary.orderCount} orders
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xl font-bold text-gray-700">
                                        ${topCustomersByRevenue.otherSummary.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Top customers by Focus Area */}
            <div className="mb-6">
                <Tooltip text="Top customers grouped by business focus area. Each category shows the top performers in Golf, Tennis, Pickleball, and Other markets.">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Top Customers by Focus Area</h3>
                </Tooltip>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {['Golf', 'Tennis', 'Pickleball', 'Other'].map(focus => (
                        <div key={focus} className="bg-white p-6 rounded-lg shadow">
                            <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                                {focus === 'Golf' && '⛳'}
                                {focus === 'Tennis' && '🎾'}
                                {focus === 'Pickleball' && '🏓'}
                                {focus === 'Other' && '🏢'}
                                <span className="ml-2">{focus}</span>
                            </h4>
                            
                            {!topCustomersByFocus[focus] || topCustomersByFocus[focus].length === 0 ? (
                                <p className="text-gray-400 text-sm text-center py-4">No data available</p>
                            ) : (
                                <div className="space-y-2">
                                    {topCustomersByFocus[focus].slice(0, 5).map((customer, index) => (
                                        <div 
                                            key={customer.id}
                                            className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-emerald-50 transition-colors"
                                        >
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <span className="text-xs font-semibold text-gray-500 w-6">#{index + 1}</span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-medium text-gray-900 break-words">
                                                        {customer.name}
                                                    </div>
                                                    <div className="text-xs text-gray-500">{customer.orderCount} orders</div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-bold text-emerald-600">
                                                    ${(customer.revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Territory Placeholder */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 p-8 rounded-lg shadow-lg text-white">
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="text-2xl font-bold mb-2">Top Customers by Location</h3>
                        <p className="text-emerald-100 mb-4">
                            Analyze customer performance by geographic region
                        </p>
                        <div className="text-sm text-emerald-200">
                            📍 Coming in Phase 2 - Location-based customer rankings
                        </div>
                    </div>
                    <div className="text-6xl opacity-20">🗺️</div>
                </div>
            </div>
        </main>
    );
};

export default CustomerReportsPage;
